import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import StarBackground from "../components/StarBackground";
import { useState } from "react";

const translations = {
  en: {
    backToMyWork: "Back to My Work",
    title: "The Heart Nebula",
    subtitle: "IC 1805 - A Cosmic Valentine in Cassiopeia",
    intro: "The Heart Nebula, also known as IC 1805 or Sharpless 2-190, is a large emission nebula located approximately 7,500 light-years from Earth in the Perseus Arm of the Milky Way Galaxy. Its distinctive heart shape makes it one of the most beloved objects in astrophotography.",
    skyLocation: "Sky Location",
    skyLocationIntro: "The Heart Nebula is located in the constellation Cassiopeia, near the Soul Nebula (IC 1848). Together they are often called the \"Heart and Soul\" nebulae and are a popular target for wide-field astrophotography.",
    coordinates: "Coordinates (J2000)",
    coordinatesDesc: "Right Ascension (RA) 2h 32m 42s, Declination (Dec) +61° 27′ 00″",
    visibility: "Visibility",
    visibilityDesc: "The Heart Nebula is best observed with telescopes equipped with narrowband filters (Hα, OIII, SII). Its large angular size of about 150 arcminutes makes it an excellent target for wide-field imaging.",
    bestTime: "Best Time to View",
    bestTimeDesc: "Best observed during autumn and winter months in the Northern Hemisphere when Cassiopeia is high overhead. Peak viewing is from September through February.",
    stellarNursery: "Stellar Nursery",
    stellarNurseryDesc: "The Heart Nebula is powered by a cluster of young stars called Melotte 15, located at its center. These hot, massive stars ionize the surrounding hydrogen gas, causing it to glow in the characteristic red color of emission nebulae."
  },
  no: {
    backToMyWork: "Tilbake til Mitt Arbeid",
    title: "Hjertetåken",
    subtitle: "IC 1805 - En Kosmisk Valentinshilsen i Kassiopeia",
    intro: "Hjertetåken, også kjent som IC 1805 eller Sharpless 2-190, er en stor emisjonståke som ligger omtrent 7 500 lysår fra Jorden i Perseus-armen av Melkeveien. Dens karakteristiske hjerteform gjør den til et av de mest elskede objektene i astrofotografi.",
    skyLocation: "Posisjon på Himmelen",
    skyLocationIntro: "Hjertetåken ligger i stjernebildet Kassiopeia, nær Sjeltåken (IC 1848). Sammen kalles de ofte \"Hjerte og Sjel\"-tåkene og er et populært mål for vidvinkel-astrofotografi.",
    coordinates: "Koordinater (J2000)",
    coordinatesDesc: "Rektascensjon (RA) 2t 32m 42s, Deklinasjon (Dec) +61° 27′ 00″",
    visibility: "Synlighet",
    visibilityDesc: "Hjertetåken observeres best med teleskoper utstyrt med smalbåndsfiltre (Hα, OIII, SII). Dens store vinkelstørrelse på omtrent 150 bueminutter gjør den til et utmerket mål for vidvinkelavbildning.",
    bestTime: "Beste Tid for Observasjon",
    bestTimeDesc: "Best observert i høst- og vintermånedene på den nordlige halvkule når Kassiopeia står høyt på himmelen. Topp observasjonstid er fra september til februar.",
    stellarNursery: "Stellær Fødestue",
    stellarNurseryDesc: "Hjertetåken drives av en klynge av unge stjerner kalt Melotte 15, som ligger i sentrum. Disse varme, massive stjernene ioniserer det omkringliggende hydrogengassen, noe som får den til å lyse i den karakteristiske røde fargen til emisjonståker."
  },
  pl: {
    backToMyWork: "Powrót do Mojej Pracy",
    title: "Mgławica Serce",
    subtitle: "IC 1805 - Kosmiczna Walentynka w Kasjopei",
    intro: "Mgławica Serce, znana również jako IC 1805 lub Sharpless 2-190, jest dużą mgławicą emisyjną położoną około 7 500 lat świetlnych od Ziemi w Ramieniu Perseusza Drogi Mlecznej. Jej charakterystyczny kształt serca czyni ją jednym z najbardziej lubianych obiektów w astrofotografii.",
    skyLocation: "Położenie na Niebie",
    skyLocationIntro: "Mgławica Serce znajduje się w gwiazdozbiorze Kasjopei, w pobliżu Mgławicy Dusza (IC 1848). Razem są często nazywane mgławicami \"Serce i Dusza\" i są popularnym celem szerokokątnej astrofotografii.",
    coordinates: "Współrzędne (J2000)",
    coordinatesDesc: "Rektascensja (RA) 2h 32m 42s, Deklinacja (Dec) +61° 27′ 00″",
    visibility: "Widoczność",
    visibilityDesc: "Mgławicę Serce najlepiej obserwować przez teleskopy wyposażone w filtry wąskopasmowe (Hα, OIII, SII). Jej duży rozmiar kątowy około 150 minut łuku czyni ją doskonałym celem do obrazowania szerokokątnego.",
    bestTime: "Najlepszy Czas na Obserwację",
    bestTimeDesc: "Najlepiej obserwować w miesiącach jesiennych i zimowych na półkuli północnej, gdy Kasjopeja jest wysoko na niebie. Szczytowy czas obserwacji to wrzesień-luty.",
    stellarNursery: "Gwiezdny Żłobek",
    stellarNurseryDesc: "Mgławica Serce jest zasilana przez gromadę młodych gwiazd o nazwie Melotte 15, znajdującą się w jej centrum. Te gorące, masywne gwiazdy jonizują otaczający gaz wodorowy, powodując jego świecenie w charakterystycznym czerwonym kolorze mgławic emisyjnych."
  }
};

export default function HeartDetails() {
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(searchParams.get('lang') || 'en');
  const t = translations[language];

  return (
    <div className="min-h-screen">
      <StarBackground />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/m31" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300">
            <ArrowLeft className="w-5 h-5" />
            <span>{t.backToMyWork}</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="text-pink-400 w-6 h-6" />
              <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                {t.title}
              </span>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'en' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('en')}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'no' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('no')}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button 
                className={`transition-colors duration-300 px-3 py-1 rounded-full ${
                  language === 'pl' 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                onClick={() => setLanguage('pl')}
                data-testid="lang-pl-btn"
              >
                PL
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <section className="relative pt-32 pb-24 px-8" data-testid="heart-details">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 text-center"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="heart-title"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-red-400 to-purple-400">
                {t.title}
              </span>
            </h1>
            <p className="text-lg text-white/90 text-center mb-8">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content Card */}
          <motion.div 
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.3)] space-y-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            {/* Overview */}
            <div>
              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.intro}
              </p>
            </div>

            {/* Sky Location */}
            <div className="pt-6 border-t border-white/10">
              <h2 
                className="text-3xl font-bold mb-6"
                style={{ fontFamily: 'Unbounded, sans-serif' }}
              >
                {t.skyLocation}
              </h2>

              <p className="text-white/90 text-lg leading-relaxed mb-6">
                {t.skyLocationIntro}
              </p>

              <div className="space-y-6 text-white/80 leading-relaxed">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.coordinates}
                  </h3>
                  <p>{t.coordinatesDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.visibility}
                  </h3>
                  <p>{t.visibilityDesc}</p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {t.bestTime}
                  </h3>
                  <p>{t.bestTimeDesc}</p>
                </div>
              </div>
            </div>

            {/* Stellar Nursery Note */}
            <div className="pt-6 border-t border-white/10">
              <div className="bg-pink-500/10 border border-pink-500/20 rounded-xl p-6">
                <h3 className="text-xl font-bold text-pink-300 mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {t.stellarNursery}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {t.stellarNurseryDesc}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
